import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { GasPump, Eye, EyeSlash } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { formatApiErrorDetail } from "../lib/api";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      await register(email, password, name);
      toast.success("Account created");
      nav("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1115] text-white flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <div className="h-10 w-10 rounded-md bg-[#FF6B00] grid place-items-center">
            <GasPump size={22} weight="fill" className="text-white" />
          </div>
          <div className="font-display font-black uppercase tracking-tight text-3xl">FuelOps</div>
        </div>
        <div className="surface rounded-lg p-6">
          <h1 className="font-display font-black uppercase tracking-tight text-2xl mb-1">Create account</h1>
          <p className="text-sm text-gray-500 mb-5">Own the pumps. Run the numbers.</p>
          <form onSubmit={submit} className="grid gap-3" data-testid="register-form">
            <label className="grid gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-gray-500">Name (optional)</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-dark"
                data-testid="register-name"
              />
            </label>
            <label className="grid gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-gray-500">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                className="input-dark"
                data-testid="register-email"
              />
            </label>
            <label className="grid gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-gray-500">Password (min 6)</span>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  minLength={6}
                  className="input-dark pr-11"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  {showPw ? <EyeSlash size={18} weight="bold" /> : <Eye size={18} weight="bold" />}
                </button>
              </div>
            </label>
            {error && (
              <div className="text-sm text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-md px-3 py-2" data-testid="register-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              data-testid="register-submit"
              className="h-14 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-60 text-white font-bold uppercase tracking-wider mt-1"
            >
              {submitting ? "Creating..." : "Create account"}
            </button>
          </form>
          <div className="text-center text-sm text-gray-500 mt-4">
            Already have an account?{" "}
            <Link to="/login" className="text-[#FF6B00] font-bold hover:underline" data-testid="link-login">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
